'use strict';

var extend = require('extend');
var Listener = require('../lib/core/Listener.js');
var DocumentObserver = require('./DocumentObserver.js');

var defaults = {
	observer : {
		root : null, // null results in viewport
		rootMargin : '-40px', // Can use 'px' or '%' units
		threshold : 0 // TODO Using anything other than 0 is causing a little bit of an issue.
	},
	listeners : null
};

/**
 * @class
 * @classdesc Wraps the browser's native IntersectionObserver. Adds and removes
 *            a class from the observed elements. This class also contains
 *            default options for IntersectionObserver.
 *            Make sure to use a polyfill for
 *            older browsers (https://github.com/WICG/IntersectionObserver/tree/gh-pages/polyfill).
 *            
 * This class triggers the following listeners:
 * <li>inView - Passed data object contains the following properties: observer, entry, element
 * <li>outView - Same as 'inView'.
 * 
 * 
 * @param {Object} [options] - 
 * @param {string|string[]} [options.styleClasses] - The style class name to watch. This can be an array or a single string.
 * @param {Object} [options.observer] - Options to pass to the native IntersectionObserver's
 *           	 	constructor (details: https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/IntersectionObserver).
 *           		Provided options will be merged with this classes default options.
 * 					Notes: `rootMargin` can use "px" or "%" for unit. `threshold` is how much of the items is in view, but make sure to consider what if the item is larger than the root.
 * @param {Object} [options.listeners] - firstInView, inView, outView
 */
function LiveIntersectionObserver(options) {
	// Options, and store ref
	var opts = this._options = extend(true, {}, defaults, options);
	
	// Adds a few basic listener methods (on, off and trigger) to this initiated object.
	Listener.init(this);
	
	// Setup any listeners that were specified in options
	this.onEach(opts.listeners);
	
	// Bind methods to 'this'
	this.addStyleClass = this.addStyleClass.bind(this);
	this.observe = this.observe.bind(this);
	this.unobserve = this.unobserve.bind(this);
	this._onObserve = this._onObserve.bind(this);
	
	// Create observer
	this.observer = new IntersectionObserver(this._onObserve, opts.observer);
	
	// Observe instances of elements styleClass as they are created, or removed
	if (opts.styleClasses === null) {
		opts.styleClasses = new Array();
	} else if (! Array.isArray(opts.styleClasses)) {
		opts.styleClasses = [opts.styleClasses];
	}
	for (var i = 0, len = opts.styleClasses.length, styleClass; i < len; i++) {
		this.addStyleClass(opts.styleClasses[i]);
	}
}

/**
 * @public
 * @param {string} styleClass - 
 */
LiveIntersectionObserver.prototype.addStyleClass = function(styleClass) {
	DocumentObserver.getInstance().onStyleClass(
		styleClass,
		this.observe,
		this.unobserve
	);
}

/**
 * @public
 * @param elem {Element|Element[]}
 */
LiveIntersectionObserver.prototype.observe = function(elems) {
	if (Array.isArray(elems)) {
		for (var i = 0, len = elems.length, elem; i < len; i++) {
			this.observer.observe(elems[i]);
		}
	} else {
		this.observer.observe(elems);
	}
}

/**
 * @public
 */
LiveIntersectionObserver.prototype.unobserve = function(elems) {
	if (Array.isArray(elems)) {
		for (var i = 0, len = elems.length, elem; i < len; i++) {
			this.observer.unobserve(elems[i]);
		}
	} else {
		this.observer.unobserve(elems);
	}
}

/**
 * @private
 */
LiveIntersectionObserver.prototype._onObserve = function(entries, observer) {
	for (var i=0, entry, elem, isFirstView, eventData; i < entries.length; i++) {
		entry = entries[i];
		elem = entry.target;
		isFirstView = !elem.classList.contains('hasFirstInView');
		eventData = {
			observer : observer,
			entry : entry,
			element : elem
		};
		
		if (entry.isIntersecting !== undefined ? entry.isIntersecting : entry.intersectionRatio > 0) {
				// Line above: Inline polyfill for Edge 15 lack of isIntersecting support. The
				// 		 polyfill we have should fix this if is loaded, but we don't load it for Edge 15.
			elem.classList.add('isInView');
			if (isFirstView) {
				elem.classList.add('hasFirstInView');
				this.trigger('firstInView', eventData);
			}
			this.trigger('inView', eventData);
		} else {
			elem.classList.remove('isInView');
			this.trigger('outView', eventData);
		}
	}
}

/**
 * @public
 */
LiveIntersectionObserver.prototype.destroy = function() {
	this.observer.disconnect();
}


module.exports = LiveIntersectionObserver;

